import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Card,
  CardMedia,
  CardActions,
  IconButton,
  Button,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CloudUpload, Delete, Visibility } from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as api from '../../services/api';
import { useI18n } from '../../i18n/useI18n';
import { Icon } from '../../types';
import { userCardEnterSx } from '../shared/UserPageShell';

const LITE_WARM = '#ff8ba7';
const LITE_PINK_DEEP = '#e86a8a';
const PRO_WARM = '#ff8ba7';
const PRO_PINK_DEEP = '#e86a8a';

export type IconChannel = 'lite' | 'pro';

const toCdnUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    if (parsed.hostname !== 'firebasestorage.googleapis.com') {
      return url;
    }
    const segments = parsed.pathname.split('/').filter(Boolean);
    const bucketIndex = segments.indexOf('b');
    const objectIndex = segments.indexOf('o');
    if (bucketIndex !== -1 && objectIndex !== -1 && objectIndex + 1 < segments.length) {
      const bucket = segments[bucketIndex + 1];
      const objectPath = segments.slice(objectIndex + 1).join('/');
      parsed.hostname = 'mphoto.mphotovn.online';
      parsed.pathname = `/${bucket}/${objectPath}`;
      return parsed.toString();
    }
    return url;
  } catch {
    return url;
  }
};

interface UserIconsProps {
  /** Nhúng trong tab M-Photo Lite / Pro — bỏ container/title riêng */
  embedded?: boolean;
  /** lite = catalog Lite; pro = catalog Pro (tách riêng) */
  channel?: IconChannel;
}

const UserIcons: React.FC<UserIconsProps> = ({ embedded = false, channel = 'lite' }) => {
  const { pages: t } = useI18n();
  const isPro = channel === 'pro';
  const accentWarm = isPro ? PRO_WARM : LITE_WARM;
  const accentDeep = isPro ? PRO_PINK_DEEP : LITE_PINK_DEEP;

  const [icons, setIcons] = useState<Icon[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchIcons = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = isPro ? await api.getProIcons() : await api.getIcons();
      setIcons(data);
    } catch (error) {
      console.error('Error fetching icons:', error);
      toast.error('Failed to load icons');
    } finally {
      setIsLoading(false);
    }
  }, [isPro]);

  useEffect(() => {
    fetchIcons();
  }, [fetchIcons]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/png': ['.png'],
    },
    maxFiles: 30,
    onDrop: async (acceptedFiles) => {
      if (!acceptedFiles.length) return;

      const invalidTypeFile = acceptedFiles.find((file) => file.type !== 'image/png');
      if (invalidTypeFile) {
        toast.error(`File ${invalidTypeFile.name} không phải PNG`);
        return;
      }

      try {
        setIsUploading(true);
        const formData = new FormData();
        acceptedFiles.forEach((file) => {
          formData.append('icons', file);
        });

        if (isPro) {
          await api.uploadProIcons(formData);
        } else {
          await api.uploadIcons(formData);
        }
        toast.success(`Upload ${acceptedFiles.length} icon thành công`);
        fetchIcons();
      } catch (error: any) {
        const message = error?.response?.data?.error || 'Failed to upload icons';
        toast.error(message);
        console.error('Upload icons error:', error);
      } finally {
        setIsUploading(false);
      }
    },
  });

  const handleDeleteIcon = async (iconId: string) => {
    try {
      if (isPro) {
        await api.deleteProIcon(iconId);
      } else {
        await api.deleteIcon(iconId);
      }
      setIcons((prev) => prev.filter((icon) => icon._id !== iconId));
      toast.success('Icon deleted successfully');
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Failed to delete icon';
      toast.error(message);
      console.error('Delete icon error:', error);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const content = (
    <>
      {!embedded && (
        <Typography variant="h4" gutterBottom>
          {isPro ? 'Pro Icons' : 'My Icons'}
        </Typography>
      )}

      <Box
        {...getRootProps()}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 1.25, sm: 2 },
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.25, sm: 2 },
          mb: 2,
          borderRadius: 2.5,
          cursor: 'pointer',
          border: '1.5px solid',
          borderColor: isDragActive ? alpha(accentDeep, 0.7) : alpha(accentDeep, 0.45),
          background: isDragActive
            ? `linear-gradient(135deg, #fff 0%, ${alpha(accentWarm, 0.22)} 100%)`
            : `linear-gradient(135deg, #fff 0%, ${alpha(accentWarm, 0.14)} 100%)`,
          boxShadow: isDragActive
            ? `0 1px 2px rgba(15,23,42,0.08), 0 10px 28px ${alpha(accentDeep, 0.22)}`
            : `0 1px 2px rgba(15,23,42,0.06), 0 8px 22px ${alpha(accentDeep, 0.14)}`,
          transition: 'all 0.22s ease',
          '&:hover': {
            borderColor: alpha(accentDeep, 0.65),
            boxShadow: `0 2px 4px rgba(15,23,42,0.08), 0 12px 28px ${alpha(accentDeep, 0.2)}`,
            transform: 'translateY(-1px)',
          },
        }}
      >
        <input {...getInputProps()} />
        <Box
          sx={{
            flexShrink: 0,
            width: { xs: 40, sm: 48 },
            height: { xs: 40, sm: 48 },
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(accentDeep, 0.16),
            color: accentDeep,
            boxShadow: `inset 0 0 0 1px ${alpha(accentDeep, 0.28)}`,
          }}
        >
          {isUploading ? <CircularProgress size={20} sx={{ color: accentDeep }} /> : <CloudUpload />}
        </Box>
        <Box sx={{ minWidth: 0, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 700, color: accentDeep, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            {isDragActive ? t.icons.dropActive : t.icons.dropIdle}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              display: { xs: 'none', sm: 'block' },
              fontSize: '0.8125rem',
              color: alpha(accentDeep, 0.7),
            }}
          >
            {t.icons.hint}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr 1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          },
          gap: { xs: 1.25, sm: 2 },
        }}
      >
        {icons.map((icon, index) => (
          <Card key={icon._id} sx={userCardEnterSx(index)}>
            <CardMedia
              component="img"
              height="200"
              image={toCdnUrl(icon.url)}
              alt={icon.name}
              sx={{
                objectFit: 'contain',
                backgroundColor: '#ffffff',
                p: 2,
              }}
            />
            <CardActions>
              <Button
                size="small"
                startIcon={<Visibility />}
                href={toCdnUrl(icon.url)}
                target="_blank"
              >
                {t.common.view}
              </Button>
              <IconButton onClick={() => handleDeleteIcon(icon._id)} color="error">
                <Delete />
              </IconButton>
            </CardActions>
          </Card>
        ))}
      </Box>
    </>
  );

  if (embedded) {
    return <Box>{content}</Box>;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {content}
    </Container>
  );
};

export default UserIcons;
